import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import throttle from 'lodash/throttle';
import { Trans } from '@lingui/react';
import { Box, Container } from '@components/layout/Grid';
import { LinkStyleUserSubHeader as anchor } from '@components/links';
import LanguageContext from '@lib/contexts/languageContext';
import { PROGRAMS_PAGE_SLUG, HEALTHCARE_PAGE_SLUG } from '@lib/constants';
import { GenericProgram, ProgramCategory } from '@gql/generated';

const NavSticky = styled.nav<{ stickyMode?: boolean }>`
  position: sticky;
  top: 0px;
  width: 100%;
  color: ${props => props.theme.colors.neutrals.white};
  padding: 15px 0;
  background-color: ${props =>
    props.stickyMode
      ? props.theme.colors.primary['blue-5']
      : props.theme.colors.neutrals['white-darker']};
  transition: background 200ms ease;
  z-index: 13;

  ${props =>
    props.stickyMode
      ? `
        border-bottom: 1px solid ${props => props.theme.shadows.light};
        box-shadow: 0 1px 1px 0 ${props => props.theme.shadows.light};
        transition: background 200ms ease-in;
      `
      : ``}
`;

const Item = styled.div<{
  programCategory: ProgramCategory;
  active?: boolean;
  stickyMode?: boolean;
}>`
  width: auto !important;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  vertical-align: bottom;
  border-bottom: 2px solid transparent;
  margin-right: 25px;
  margin-left: 0;
  cursor: pointer;

  color: ${({ active, theme, programCategory, stickyMode }) => {
    if (stickyMode) {
      return theme.colors.neutrals.white;
    } else if (active) {
      return programCategory === ProgramCategory['Challenges']
        ? theme.colors.primary['green']
        : theme.colors.primary['blue-2'];
    } else {
      return theme.colors.neutrals.gray;
    }
  }};

  ${({ programCategory, active, theme }) => {
    return active
      ? `border-bottom: 2px solid ${
          programCategory === ProgramCategory['Challenges']
            ? theme.colors.primary['green']
            : theme.colors.primary['blue-2']
        };`
      : '';
  }}
`;

const Anchor = styled(anchor)`
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
`;

const ProgramsLink = styled(Anchor)<{ stickyMode: boolean }>`
  color: ${props =>
    props.stickyMode
      ? props.theme.colors.neutrals.white
      : props.theme.colors.neutrals.gray};
  font-size: 13px;
  font-weight: 500;
`;

type Props = {
  programCategory?: GenericProgram['category'];
  overviewLink: string;
  progressLink?: string;
  hasAccess: boolean;
  showListingLink?: boolean;
};

type LISTING_PAGES = Partial<
  Record<ProgramCategory, { slug: string; text: React.ReactNode }>
>;
const listing_pages: LISTING_PAGES = {
  [ProgramCategory['Courses']]: {
    slug: 'courses',
    text: <Trans id="courses_2021.other_courses" message="Other Courses" />,
  },
  [ProgramCategory['Programs']]: {
    slug: PROGRAMS_PAGE_SLUG,
    text: <Trans id="metadeta.programs.other_programs" />,
  },
  [ProgramCategory['Challenges']]: {
    slug: PROGRAMS_PAGE_SLUG,
    text: <Trans id="metadeta.programs.other_programs" />,
  },
  [ProgramCategory['Healthcare']]: {
    slug: HEALTHCARE_PAGE_SLUG,
    text: <Trans id="metadeta.programs.other_programs" />,
  },
  [ProgramCategory['LiveCourse']]: {
    slug: PROGRAMS_PAGE_SLUG,
    text: <Trans id="metadeta.programs.other_programs" />,
  },
};

const ProgramSubHeader = (props: Props) => {
  const router = useRouter();
  const lang = useContext(LanguageContext);
  const ref = useRef<HTMLElement>();
  const [stickyNav, setStickyPosition] = useState(false);
  const { showListingLink = true } = props;

  const monitorScroll = () => {
    if (ref.current) {
      const y = Math.abs(ref.current.getBoundingClientRect()?.y);
      if (y <= 10) {
        setStickyPosition(true);
      } else {
        setStickyPosition(false);
      }
    }
  };

  useEffect(() => {
    const callback = throttle(monitorScroll, 200, {
      leading: true,
      trailing: true,
    });
    window.addEventListener('scroll', callback);
    return () => window.removeEventListener('scroll', callback);
  }, []);

  const links = [
    {
      pathname: props.overviewLink,
      active: [
        '/[lang]/programs/[programId]/overview',
        '/[lang]/courses/[programId]/overview',
      ].includes(router.route),
      text: <Trans id="metadata.programs.overview" />,
    },
  ];

  if (props.hasAccess) {
    links.push({
      pathname: props.progressLink,
      active: [
        '/[lang]/programs/[programId]/[...params]',
        '/[lang]/courses/[programId]/[...params]',
      ].includes(router.route),
      text: <Trans id="metadata.programs.my_progress" />,
    });
  }

  const LISTING_PAGE =
    listing_pages[props.programCategory || ProgramCategory['Programs']];

  return (
    <NavSticky ref={ref} stickyMode={stickyNav}>
      <Container pl={[20, 20, 10]} pr={[20, 20, 10]} alignItems="flex-end">
        {links.reverse().map((item, index) => {
          return (
            <Item
              key={index}
              active={item.active}
              stickyMode={stickyNav}
              programCategory={props.programCategory}
            >
              <Anchor href={item.pathname}>{item.text}</Anchor>
            </Item>
          );
        })}
        {showListingLink ? (
          <Box ml="auto" mr={0}>
            <ProgramsLink
              href={`/${lang}/${LISTING_PAGE.slug}`}
              stickyMode={stickyNav}
            >
              {LISTING_PAGE.text}
            </ProgramsLink>
          </Box>
        ) : null}
      </Container>
    </NavSticky>
  );
};

export default ProgramSubHeader;
